/**
 * OtpInput: one input box for the whole code. Numeric keypad, paste, and SMS/Telegram autofill all work
 * because it is a single field. Calls onComplete when the last digit is entered (the caller submits the form).
 */
import { useRef } from "react";

export interface OtpInputProps {
  value: string;
  onChange: (value: string) => void;
  /** Called with the full code and the owning form (if any) once all digits are filled. */
  onComplete?: (code: string, form: HTMLFormElement | null) => void;
  length?: number;
  disabled?: boolean;
  describedBy?: string;
  autoFocus?: boolean;
}

export function OtpInput({ value, onChange, onComplete, length = 6, disabled, describedBy, autoFocus }: OtpInputProps) {
  const ref = useRef<HTMLInputElement>(null);

  const handleChange = (raw: string) => {
    const clean = raw.replace(/\D/g, "").slice(0, length);
    onChange(clean);
    if (clean.length === length) {
      const form = ref.current?.form ?? null;
      window.setTimeout(() => onComplete?.(clean, form), 0);
    }
  };

  return (
    <input
      ref={ref}
      value={value}
      onChange={(e) => handleChange(e.target.value)}
      onPaste={(e) => {
        e.preventDefault();
        handleChange(e.clipboardData.getData("text"));
      }}
      inputMode="numeric"
      pattern="[0-9]*"
      autoComplete="one-time-code"
      autoFocus={autoFocus}
      maxLength={length}
      disabled={disabled}
      placeholder={"0".repeat(length)}
      aria-label={`${length}-digit code`}
      aria-describedby={describedBy}
      className="field w-full py-3 text-center font-mono text-xl tracking-[0.4em] sm:text-2xl disabled:opacity-60"
    />
  );
}
